/**
 * Map feature scope registry — single source of truth for the standard
 * (built-in) overlay layers and the country in which each is available.
 *
 * Every standard layer carries a `scope` (an ISO 3166-1 alpha-2 country code, or
 * `undefined` for a globally-available layer), exactly like a country-restricted
 * basemap (see {@link BasemapDefinition.scope} in `maplibre.ts`). All consumers
 * — the layers panel, the share form, the redux force-off, and the default
 * visibility map — derive from this one registry. Adding another country or a
 * global layer is a single new entry here (and the BE mirror in
 * `WorkspaceConstants.STANDARD_LAYER_SCOPES`).
 */

import { POLAND_COUNTRY_CODE } from '../constants/country';
import type { LayerVisibility } from '../types/layers/MapLayer';

/**
 * Feature availability scope: an ISO 3166-1 alpha-2 country code (e.g. `'PL'`).
 * `undefined` on a definition means the feature is available in every country.
 */
export type Scope = string;

/** Definition of one standard (built-in) overlay layer. */
export interface StandardLayerDefinition {
  /** Typed visibility key — cross-checked against {@link LayerVisibility}. */
  key: keyof LayerVisibility;
  /** i18n key — a string LITERAL (never assembled at runtime, breaks extraction). */
  labelKey: string;
  /** Country scope; `undefined` => available in every country. */
  scope?: Scope;
  /** Default visibility — feeds the derived {@link DEFAULT_VISIBILITY} map. */
  defaultVisible: boolean;
}

/**
 * The standard overlay layers. `scope: 'PL'` because their underlying data
 * (national / voivodeship borders and roads) only exists for Poland.
 * `defaultVisible` MUST stay in sync with the values the app ships — see
 * {@link DEFAULT_VISIBILITY}.
 */
export const STANDARD_LAYER_DEFINITIONS: StandardLayerDefinition[] = [
  {
    key: 'polandBorder',
    labelKey: 'layers.polandBorder',
    scope: POLAND_COUNTRY_CODE,
    defaultVisible: true,
  },
  {
    key: 'voivodeshipBorders',
    labelKey: 'layers.voivodeshipBorders',
    scope: POLAND_COUNTRY_CODE,
    defaultVisible: false,
  },
  {
    key: 'nationalRoads',
    labelKey: 'layers.nationalRoads',
    scope: POLAND_COUNTRY_CODE,
    defaultVisible: true,
  },
  {
    key: 'voivodeshipRoads',
    labelKey: 'layers.voivodeshipRoads',
    scope: POLAND_COUNTRY_CODE,
    defaultVisible: true,
  },
];

/**
 * Whether a feature with the given scope is available for a workspace's country.
 * Shared by layers AND basemaps. A feature with no scope is always available;
 * otherwise the codes must match (case-insensitive). Null / empty country → only
 * unscoped (global) features are available.
 */
export function isFeatureInScope(
  featureScope: Scope | undefined,
  countryCode: string | null
): boolean {
  if (!featureScope) {
    return true;
  }
  return featureScope.toUpperCase() === (countryCode ?? '').toUpperCase();
}

/** Standard layer definitions available for a workspace's country. */
export function layersAllowedForCountry(
  countryCode: string | null
): StandardLayerDefinition[] {
  return STANDARD_LAYER_DEFINITIONS.filter((definition) =>
    isFeatureInScope(definition.scope, countryCode)
  );
}

/**
 * Whether any standard layer is available for a workspace's country. Cheaper
 * than `layersAllowedForCountry(...).length > 0` for the map render hot path —
 * short-circuits without allocating a filtered array.
 */
export function hasLayersForCountry(countryCode: string | null): boolean {
  return STANDARD_LAYER_DEFINITIONS.some((definition) =>
    isFeatureInScope(definition.scope, countryCode)
  );
}

/** Whether a single standard layer is available for a workspace's country. */
export function isLayerAllowedForCountry(
  key: keyof LayerVisibility,
  countryCode: string | null
): boolean {
  const definition = STANDARD_LAYER_DEFINITIONS.find(
    (candidate) => candidate.key === key
  );
  return definition ? isFeatureInScope(definition.scope, countryCode) : true;
}

/** All standard layer keys, in registry order. */
export function standardLayerKeys(): (keyof LayerVisibility)[] {
  return STANDARD_LAYER_DEFINITIONS.map((definition) => definition.key);
}

/**
 * Standard layer keys NOT available for a workspace's country — the set to
 * force off when hydrating layer visibility for that workspace.
 */
export function scopedOutLayerKeys(
  countryCode: string | null
): (keyof LayerVisibility)[] {
  return STANDARD_LAYER_DEFINITIONS.filter(
    (definition) => !isFeatureInScope(definition.scope, countryCode)
  ).map((definition) => definition.key);
}

/**
 * Default visibility for the standard layers, derived from each definition's
 * `defaultVisible`. Re-exported through `types/layers` so existing import paths
 * stay unchanged.
 */
export const DEFAULT_VISIBILITY: LayerVisibility =
  STANDARD_LAYER_DEFINITIONS.reduce((visibility, definition) => {
    visibility[definition.key] = definition.defaultVisible;
    return visibility;
  }, {} as LayerVisibility);
